import React, { ReactNode } from 'react';
import { Trans } from 'react-i18next';

import WaitingIcon from '@mui/icons-material/HourglassBottom';
import SuccessIcon from '@mui/icons-material/CheckCircleOutline';
import WarningIcon from '@mui/icons-material/WarningAmber';
import ErrorIcon from '@mui/icons-material/ErrorOutline';

import { Box, CircularProgress, Typography } from '@mui/material';

import { JobModel } from 'models';
import { formatDate } from 'utils/dates';
import { PerformanceBasedPayCreateJobData } from 'services';

interface PerformanceBasedPayProgressInfoSectionProps {
  title?: ReactNode;
  subtitle?: ReactNode;
  graphic?: ReactNode;
  textAlign?: 'center';
}

function PerformanceBasedPayProgressInfoSection({
  title,
  subtitle,
  graphic,
  textAlign,
}: PerformanceBasedPayProgressInfoSectionProps) {
  return (
    <Box display="flex" flexDirection="column" py={2}>
      <Box display="flex" justifyContent="center" mb={title ? 2 : 0}>
        {graphic}
      </Box>
      {title ? (
        <Box display="flex" justifyContent={textAlign} mb={subtitle ? 2 : 0}>
          <Typography textAlign={textAlign}>{title}</Typography>
        </Box>
      ) : null}
      {subtitle ? (
        <Box display="flex" justifyContent={textAlign}>
          <Typography textAlign={textAlign}>{subtitle}</Typography>
        </Box>
      ) : null}
    </Box>
  );
}

interface PerformanceBasedPayProgressInfoActiveProps {
  workerCount: number;
  successCount: number;
  errorCount: number;
  period: string;
}

function PerformanceBasedPayProgressInfoActive({
  successCount,
  workerCount,
  errorCount,
  period,
}: PerformanceBasedPayProgressInfoActiveProps) {
  const percent = workerCount ? Math.round(((successCount + errorCount) / workerCount) * 100) : 0;

  return (
    <Box>
      <Box display="flex" justifyContent="center" flexDirection="column" alignItems="center" mb={2}>
        <Box mb={2}>
          <Typography textAlign="center">
            <Trans
              i18nKey="NEW_PERFORMANCE_PAY_DIALOG.JOB_IS_RUNNING"
              values={{
                period,
              }}
            />
          </Typography>
        </Box>

        <Box mb={1}>
          <CircularProgress />
        </Box>
        <Typography>{percent}%</Typography>
      </Box>
      <Box display="flex" flexDirection="column">
        <Box mb={1}>
          <Typography>
            <Trans
              i18nKey="NEW_PERFORMANCE_PAY_DIALOG.JOB_WORKER_COUNT"
              values={{
                count: workerCount,
              }}
            />
          </Typography>
        </Box>
        <Box mb={1}>
          <Typography>
            <Trans
              i18nKey="NEW_PERFORMANCE_PAY_DIALOG.JOB_SUCCESS_COUNT"
              values={{
                count: successCount,
              }}
            />
          </Typography>
        </Box>
        <Box mb={1}>
          <Typography>
            <Trans
              i18nKey="NEW_PERFORMANCE_PAY_DIALOG.JOB_ERROR_COUNT"
              values={{
                count: errorCount,
              }}
            />
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

interface PerformanceBasedPayProgressInfoSuccessProps {
  count: number;
  period: string;
}

function PerformanceBasedPayProgressInfoSuccess({
  count,
  period,
}: PerformanceBasedPayProgressInfoSuccessProps) {
  return (
    <Box display="flex" py={2} flexDirection="column" alignItems="center">
      <Box mb={2}>
        <SuccessIcon color="success" fontSize="large" />
      </Box>
      <Typography textAlign="center">
        <Trans
          i18nKey="NEW_PERFORMANCE_PAY_DIALOG.JOB_SUCCESS_MESSAGE"
          values={{
            count,
            period,
          }}
        />
      </Typography>
    </Box>
  );
}

interface PerformanceBasedPayProgressInfoErrorProps {
  successCount: number;
  errorCount: number;
  period: string;
}

function PerformanceBasedPayProgressInfoError({
  successCount,
  errorCount,
  period,
}: PerformanceBasedPayProgressInfoErrorProps) {
  return (
    <>
      {successCount > 0 ? (
        <PerformanceBasedPayProgressInfoSection
          graphic={<WarningIcon color="warning" fontSize="large" />}
          title={
            <Trans
              i18nKey="NEW_PERFORMANCE_PAY_DIALOG.JOB_SUCCESS_MESSAGE"
              values={{
                count: successCount,
                period,
              }}
            />
          }
          subtitle={
            <Trans
              i18nKey="NEW_PERFORMANCE_PAY_DIALOG.JOB_PARTIALLY_ERROR_MESSAGE"
              values={{
                count: errorCount,
              }}
            />
          }
        />
      ) : null}

      {successCount === 0 ? (
        <PerformanceBasedPayProgressInfoSection
          graphic={<ErrorIcon color="error" fontSize="large" />}
          textAlign="center"
          title={
            <Trans
              i18nKey="NEW_PERFORMANCE_PAY_DIALOG.JOB_ERROR_MESSAGE"
              values={{
                period,
              }}
            />
          }
        />
      ) : null}
    </>
  );
}

export interface PerformanceBasedPayProgressInfoProps {
  isActiveJobFetching?: boolean;
  job?: JobModel<PerformanceBasedPayCreateJobData> | null;
}

export function PerformanceBasedPayProgressInfo({
  job,
  isActiveJobFetching,
}: PerformanceBasedPayProgressInfoProps) {
  const successCount = job?.data?.successCount ?? 0;
  const errorCount = job?.data?.errorCount ?? 0;
  const workerCount = job?.data?.workerCount ?? 0;

  const formattedPeriod =
    job?.data?.data?.startDate && job?.data?.data?.endDate
      ? `${formatDate(job.data.data.startDate)} - ${formatDate(job.data.data.endDate)}`
      : '';

  return (
    <>
      {isActiveJobFetching ? (
        <PerformanceBasedPayProgressInfoSection graphic={<CircularProgress />} />
      ) : null}

      {!isActiveJobFetching && job?.status === 'waiting' ? (
        <PerformanceBasedPayProgressInfoSection
          graphic={<WaitingIcon color="primary" fontSize="large" />}
          title={<Trans i18nKey="NEW_PERFORMANCE_PAY_DIALOG.JOB_WAT_FOR_RUN" />}
          textAlign="center"
        />
      ) : null}

      {!isActiveJobFetching && job?.status === 'active' ? (
        <PerformanceBasedPayProgressInfoActive
          workerCount={workerCount}
          successCount={successCount}
          errorCount={errorCount}
          period={formattedPeriod}
        />
      ) : null}

      {!isActiveJobFetching && job?.status === 'completed' && errorCount === 0 ? (
        <PerformanceBasedPayProgressInfoSuccess count={successCount} period={formattedPeriod} />
      ) : null}

      {!isActiveJobFetching && job?.status === 'completed' && errorCount > 0 ? (
        <PerformanceBasedPayProgressInfoError
          successCount={successCount}
          errorCount={errorCount}
          period={formattedPeriod}
        />
      ) : null}
    </>
  );
}
